import React, { useContext, useCallback, useMemo } from "react";
import { map, filter, isNumber, sortBy } from "lodash";

import { LineupContext } from "../../context/LineupContext";
import { type TeamFilter, usePickerContext } from "../../context/PickerContext";
import { TeamID, LineupOption } from "../../types";
import {
  usePickerStateSelector,
  usePickerActionDispatch,
} from "../../context/pickerState";

import { PickerOption } from "./PickerOption";
import { TeamFilterButton } from "./TeamFilterButton";
import { TopBar } from "./TopBar";
import { ConfirmDialog } from "./ConfirmDialog";

const EMPTY_FILTER: TeamFilter = { teams: [] };

const TeamFilterBox = React.memo(() => {
  const { selected, teams } = usePickerStateSelector(
    (state) => state.teamFilter ?? EMPTY_FILTER
  );

  const dispatch = usePickerActionDispatch();

  const toggleTeamFilterButton = useCallback(
    (teamId: TeamID) => {
      dispatch({ type: "TOGGLE_TEAM_FILTER", payload: { teamId } });
    },
    [dispatch]
  );

  const mid = Math.ceil(teams.length / 2);
  const teamRows = [
    teams.slice(0, mid), teams.slice(mid)
  ];

  return (
    <div className="neo__lineupPicker__teamFilter">
      {map(teamRows, (rowTeams, rowIndex) => {
        return <div className="neo__lineupPicker__teamFilter__row" key={rowIndex}>
          {map(rowTeams, (teamId) => {
            const isSelected = selected === teamId;

            return (
              <TeamFilterButton
                key={teamId}
                toggle={toggleTeamFilterButton}
                teamId={teamId}
                markUnselected={(selected && !isSelected) || false}
              />
            );
          })}
        </div>
      })}
    </div>
  );
});

const useOptionsToRender = () => {
  const { options, selectedTeam } = usePickerStateSelector((state) => ({
    options: state.openFor?.options,
    selectedTeam: state.teamFilter?.selected,
  }));

  return useMemo(() => {
    return sortBy(
      filter(options, (opt) => !selectedTeam || opt.team === selectedTeam),
      (option) => -option.price,
      (option) => option.short_name ?? option.name
    );
  }, [options, selectedTeam]);
};

const OptionsList = React.memo(() => {
  const optionsToRender = useOptionsToRender();

  return (
    <div className="neo__lineupPicker__list">
      <div className="neo__lineupPicker__options">
        {map(optionsToRender, (option) => {
          return <PickerOption key={option.id} {...option} />;
        })}
      </div>
    </div>
  );
});

export const useHandlePick = () => {
  const { lineup, getSlotIfPicked, isAlreadyInPicks } =
    useContext(LineupContext);

  const { openPromiseResolveRef } = usePickerContext();

  const openFor = usePickerStateSelector((state) => state.openFor);

  const dispatch = usePickerActionDispatch();

  return useCallback(
    (
      option: LineupOption | undefined,
      config?: { skipCheckConfirm?: boolean; clearSlot?: boolean }
    ) => {
      if (!lineup || !openFor) {
        !lineup && console.error("No lineup available");
        !openFor && console.error("No opened option available");
        return;
      }

      const prevPickAtThisSlot =
        openFor && getSlotIfPicked(openFor.slot)?.pickedOption;

      const boughtFor = lineup.slots?.[openFor.slot]?.bought_for_budget;

      // make the pick!
      openPromiseResolveRef.current?.({
        option,
        clearSlot: config?.clearSlot,
      });
      openPromiseResolveRef.current = undefined;
      dispatch({ type: "RESET_STATE" });
    },
    [openFor, lineup, dispatch, getSlotIfPicked, isAlreadyInPicks]
  );
};

export const Picker = () => {
  const openFor = usePickerStateSelector((state) => state.openFor);

  const { forceClose } = usePickerContext();

  if (!openFor) {
    return null;
  }

  return (
    <div className="neo__lineupPicker">
      <div className="neo__lineupPicker__backdrop" onClick={forceClose} />
      <div className="neo__lineupPickerPanel">
        <TopBar />
        <TeamFilterBox />
        <OptionsList />
        <ConfirmDialog />
      </div>
    </div>
  );
};
